<template>
  <header @keydown.esc="closeAll">
    <div
      class="mx-auto px-4 sm:px-6 lg:px-10 py-4 flex items-center justify-between my-4"
      @click.stop
    >
      <!-- Logo -->
      <div class="text-2xl font-bold text-blue">
        <NuxtLink to="/"><img :src="logo" alt="logo" class="h-8" /></NuxtLink>
      </div>

      <!-- Desktop Nav -->
      <nav class="hidden md:flex gap-8 items-center relative">
        <NuxtLink
          to="/"
          :class="currentPath === '/' || currentPath === '' ? 'active' : ''"
          class="font-poppins"
          >Inicio</NuxtLink
        >

        <!-- Dropdown -->
        <div class="relative" @mouseover="toggleDropdown(true)">
          <NuxtLink
            to="/formaciones"
            class="text-gray-800 hover:text-blue flex items-center gap-1"
            :aria-expanded="isDropdownOpen ? 'true' : 'false'"
            aria-haspopup="menu"
          >
            Todos los cursos
            <svg
              class="w-4 h-4 text-gray-500 transition"
              :class="isDropdownOpen ? 'rotate-180 text-blue' : ''"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </NuxtLink>

          <!-- Dropdown menu -->
          <div
            v-show="isDropdownOpen"
            class="absolute bg-white shadow-lg rounded-md top-full left-0 mt-2 w-56 z-50"
            @click.stop
          >
            <NuxtLink
              v-if="categories.length > 0"
              v-for="category in categories"
              :to="'/formaciones?category=' + category.name"
              class="block px-4 py-2 text-gray-700 hover:bg-gray-100 transition"
              >{{ category.name }}</NuxtLink
            >
          </div>
        </div>

        <a
          href="https://selectalent.es/"
          target="_blank"
          class="text-gray-800 hover:text-blue"
          >Sobre Nosotros</a
        >
        <NuxtLink to="/#features" class="text-gray-800 hover:text-blue"
          >¿Por qué elegir nuestros talleres?</NuxtLink
        >
      </nav>

      <!-- Contact Button -->
      <div class="hidden md:block">
        <NuxtLink
          to="/#contact"
          class="bg-blue text-white px-5 py-3 rounded-md shadow-md hover:bg-blue-700 transition"
        >
          Contacto
        </NuxtLink>
      </div>

      <!-- Mobile Menu Button -->
      <button
        @click.stop="isMobileOpen = !isMobileOpen"
        class="md:hidden text-gray-700 focus:outline-none"
        :aria-expanded="isMobileOpen ? 'true' : 'false'"
        aria-controls="mobile-menu"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="h-6 w-6"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M4 6h16M4 12h16M4 18h16"
          />
        </svg>
      </button>
    </div>

    <!-- Mobile Nav -->
    <transition name="fade">
      <div
        v-if="isMobileOpen"
        id="mobile-menu"
        class="md:hidden bg-white shadow-inner px-4 py-4 transition-all duration-300"
        @click.stop
      >
        <nav class="flex flex-col gap-3">
          <NuxtLink
            to="/"
            :class="currentPath === '/' ? 'active' : ''"
            class="text-blue font-semibold"
            >Inicio</NuxtLink
          >

          <details class="group">
            <summary
              class="cursor-pointer text-gray-800 flex justify-between items-center"
            >
              Todos los cursos
              <svg
                class="w-4 h-4 text-gray-500 group-open:rotate-180 transition"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </summary>
            <div class="ml-4 mt-2 space-y-2">
              <NuxtLink
                v-if="categories.length > 0"
                v-for="category in categories"
                :to="'/formaciones?category=' + category.name"
                class="block text-sm text-gray-600"
                >{{ category.name }}</NuxtLink
              >
            </div>
          </details>

          <a
            href="https://selectalent.es/"
            target="_blank"
            class="text-gray-800"
            >Sobre Nosotros</a
          >
          <NuxtLink href="/#features" class="text-gray-800"
            >¿Por qué elegir nuestros talleres?</NuxtLink
          >
          <NuxtLink
            to="/#contact"
            class="bg-blue text-white text-center py-2 rounded-md shadow hover:bg-blue-700"
          >
            Contacto
          </NuxtLink>
        </nav>
      </div>
    </transition>
  </header>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from "vue";
import logo from "@/assets/img/global/logo.png";
import { useDataStore } from "~/stores/data";
import { useRoute } from "vue-router";
const route = useRoute();
const currentPath = computed(() => route.path);
const { categories } = useDataStore();

const isMobileOpen = ref(false);
const isDropdownOpen = ref(false);

const closeAll = () => {
  isMobileOpen.value = false;
  isDropdownOpen.value = false;
};

const toggleDropdown = (boolean?: boolean) => {
  if (boolean) {
    isDropdownOpen.value = boolean;
    return;
  }
  isDropdownOpen.value = !isDropdownOpen.value;
};

const onWindowClick = () => closeAll();

onMounted(() => {
  window.addEventListener("click", onWindowClick, { passive: true });
});

onBeforeUnmount(() => {
  window.removeEventListener("click", onWindowClick);
});
watch(() => route.path, closeAll);
</script>

<style scoped>
* {
  font-family: "Poppins", sans-serif;
}
.active {
  color: #0d72ff;
  font-weight: 600;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
